
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { cn } from "@/lib/utils"
import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import localeData from '@/data/locale.json'
import { LanguageCode, LANGUAGES } from '@/data/languages'
interface LanguageOption {
    value: string
    label: string
    langCode: string
}

interface LanguageSelectProps {
    value?: string
    onChange: (value: string) => void
    existingLocales: string[]
    placeholder?: string
    emptyMessage?: string
    searchPlaceholder?: string
    className?: string
    disabled?: boolean
}

const getFlagEmoji = (countryCode: string) => {
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0))
    return String.fromCodePoint(...codePoints)
}

const createLanguageOptions = (existingLocales: string[]): LanguageOption[] => {
    const primaryDialects = localeData['primary-dialects'] as Record<string, string>
    const locales = Object.values(primaryDialects)
        .filter((locale) => !existingLocales.includes(locale))
        .sort()

    return locales.map((locale) => {
        const langCode = locale.split('-')[0]
        return {
            value: locale,
            label: locale,
            langCode,
        }
    })
}

/**
 * Language select component with search functionality
 * Allows selecting a single language from available locales
 */
export const LanguageSelect = ({
    value,
    onChange,
    existingLocales,
    placeholder,
    emptyMessage,
    searchPlaceholder,
    className,
    disabled = false,
}: LanguageSelectProps) => {
    const { t } = useTranslation('common')
    const resolvedPlaceholder = placeholder ?? t('languageSelect.placeholder')
    const resolvedEmptyMessage = emptyMessage ?? t('languageSelect.noLanguagesFound')
    const resolvedSearchPlaceholder = searchPlaceholder ?? t('languageSelect.searchPlaceholder')
    const [open, setOpen] = React.useState(false)

    const options = React.useMemo(() => {
        return createLanguageOptions(existingLocales)
    }, [existingLocales])

    const selectedOption = React.useMemo(() => {
        return options.find((option) => option.value === value)
    }, [options, value])

    const handleSelect = (selectedValue: string) => {
        onChange(selectedValue === value ? '' : selectedValue)
        setOpen(false)
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn('w-full justify-between', className)}
                >
                    {selectedOption ? (
                        <span className="flex items-center gap-2">
                            <span>{getFlagEmoji(selectedOption.langCode)}</span>
                            <span>{selectedOption.label}</span>
                        </span>
                    ) : (
                        <span className="text-muted-foreground">{resolvedPlaceholder}</span>
                    )}
                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start" style={{ 'zIndex': '10000' }}>
                <Command>
                    <CommandInput placeholder={resolvedSearchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>{resolvedEmptyMessage}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = value === option.value

                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => handleSelect(option.value)}
                                    >
                                        <div
                                            className={cn(
                                                'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                                isSelected
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'opacity-50 [&_svg]:invisible',
                                            )}
                                        >
                                            <CheckIcon className="h-4 w-4" />
                                        </div>
                                        <span className="mr-2">{getFlagEmoji(LANGUAGES[option.langCode as LanguageCode].flagCode)}</span>
                                        <span>{LANGUAGES[option.langCode as LanguageCode].name}</span>
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}

